// ============================================================
// Craft a Brew — Recipe Builder Enhancements v1.7.17
// Date: April 14, 2026
// Load AFTER recipe-builder.js. This file layers new features
// on top of the existing calculator without modifying it.
//
// Features added:
//   1. Fermentable name cleanup (removes duplicate parentheses)
//   2. Sticky category headers in fermentables dropdown
//   3. localStorage auto-save with 7-day expiration
//   4. Auto-restore saved recipe with welcome-back banner
//   5. Visual dropdown chevron on searchable selects
//   6. Smart leave-page warning (only if localStorage unavailable)
//
// Changelog (trimmed):
//   v1.7.7 - production baseline (all pre-yeast-restore-work features)
//   v1.7.17 - ROLLBACK. After eight rounds of attempting to auto-
//          restore the yeast selection on page reload (v1.7.8 through
//          v1.7.16), every approach either failed to update the
//          search-select wrapper's visible display or introduced
//          regressions (SRM badge showing 0, scroll jumps, flashing
//          dropdowns, race conditions with the yeast packet weight
//          retry loop).
//          Decision: roll back to v1.7.7 code exactly, with two
//          minimal additive changes:
//          (a) restoreRecipe() skips the yeast restore block
//              entirely — the yeast IS still saved to localStorage
//              so nothing is lost, but it's not auto-applied on
//              page reload
//          (b) showRestoreBanner() now takes the full state object
//              and displays "Last used yeast: <name> — please
//              re-select below" in the welcome-back banner, so the
//              user has a one-click path to re-pick the same yeast
//          Everything else — setupYeastWeightDisplay, the retry
//          loop, the MutationObserver, updateYeastDropdownWeights,
//          the dispatchEvent('change') — is identical to v1.7.7
//          baseline. Zero functional changes to any working
//          behavior.
// ============================================================

(function () {
  'use strict';

  var STORAGE_KEY = 'cab_recipe_builder_v1';
  var EXPIRY_DAYS = 7;
  var isRestoring = false;
  var saveTimer = null;

  // Wait until recipe-builder.js has finished initializing
  function ready(fn) {
    if (window.PRODUCTS && typeof window.recalculate === 'function') {
      fn();
    } else {
      setTimeout(function () { ready(fn); }, 100);
    }
  }

  ready(init);

  function init() {
    try {
      cleanFermentableNames();
      injectStyles();
      wrapRecalculate();
      setupLeavePageWarning();
      restoreRecipe();
      replaceEmojisWithIcons();
      repositionMatchDetails();
      setupHopAADisplay();
      setupHopInfoBars();
      updateHopsNote();
      setupYeastWeightDisplay();
      setTimeout(updateBeerGlassForLowSRM, 200);
      console.log('[CAB] Recipe builder enhancements v1.7.17 loaded.');
    } catch (e) {
      console.error('[CAB] Enhancements failed to initialize:', e);
    }
  }

  function repositionMatchDetails() {
    var beerPreview = document.querySelector('.cab-rb .beer-preview');
    var matchDetails = document.querySelector('.cab-rb #matchDetails');
    if (!beerPreview || !matchDetails) return;
    if (beerPreview.parentNode) {
      beerPreview.parentNode.insertBefore(matchDetails, beerPreview.nextSibling);
    }
  }

  // ============================================================
  // 1. Fermentable name cleanup
  // ============================================================
  function cleanFermentableNames() {
    if (!window.PRODUCTS || !window.PRODUCTS.fermentables) return;

    var nameMap = {
      'Maltodextrin (Maltodextrin (1.6 oz))': 'Maltodextrin (1.6 oz)',
      'Maltodextrin (Maltodextrin (8 oz))': 'Maltodextrin (8 oz)',
      'Lactose (Milk Sugar) (4 oz Lactose)': 'Lactose / Milk Sugar (4 oz)',
      'Lactose (Milk Sugar) (1 lb Lactose)': 'Lactose / Milk Sugar (1 lb)',
      'Lactose (Milk Sugar) (20 oz Lactose)': 'Lactose / Milk Sugar (20 oz)',
      'Dark Belgian Candi Sugar (Dark Belgian Candi Sugar (1.6 oz))': 'Dark Belgian Candi Sugar (1.6 oz)',
      'Dark Belgian Candi Sugar (Dark Belgian Candi Sugar (8 oz))': 'Dark Belgian Candi Sugar (8 oz)',
      'Dark Belgian Candi Sugar (Dark Belgian Candi Sugar (1 lb))': 'Dark Belgian Candi Sugar (1 lb)',
      'Light Belgian Candi Sugar (Light Belgian Candi Sugar (1.6 oz))': 'Light Belgian Candi Sugar (1.6 oz)',
      'Light Belgian Candi Sugar (Light Belgian Candi Sugar (8 oz))': 'Light Belgian Candi Sugar (8 oz)',
      'Light Belgian Candi Sugar (Light Belgian Candi Sugar (1 lb))': 'Light Belgian Candi Sugar (1 lb)',
      'Brewers Crystals (Brewers Crystals (2 oz))': 'Brewers Crystals (2 oz)',
      'Brewers Crystals (Brewers Crystals (10 oz))': 'Brewers Crystals (10 oz)',
      'Brewers Crystals (Brewers Crystals (1 lb))': 'Brewers Crystals (1 lb)'
    };

    window.PRODUCTS.fermentables.forEach(function (f) {
      if (nameMap[f.name]) {
        f.name = nameMap[f.name];
      }
    });
  }

  // ============================================================
  // 2 + 5. CSS injection — sticky headers, chevron, banner
  // ============================================================
  function injectStyles() {
    var css = [
      '.search-select-dd .search-select-group {',
      '  position: sticky !important;',
      '  top: 0 !important;',
      '  z-index: 5 !important;',
      '  background: #f5f3ee !important;',
      '  box-shadow: 0 1px 0 rgba(0,0,0,0.05);',
      '}',

      '.cab-rb .search-select-input {',
      '  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'14\' height=\'14\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%237a756d\' stroke-width=\'2.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'/%3e%3c/svg%3e") !important;',
      '  background-repeat: no-repeat !important;',
      '  background-position: right 12px center !important;',
      '  background-color: #ffffff !important;',
      '  padding-right: 34px !important;',
      '  cursor: pointer !important;',
      '}',

      '.cab-rb .cab-icon,',
      '.cab-rb svg.cab-icon {',
      '  display: inline-block !important;',
      '  vertical-align: -0.22em !important;',
      '  width: 1.5em !important;',
      '  height: 1.5em !important;',
      '  stroke: currentColor !important;',
      '  stroke-width: 1.8 !important;',
      '  fill: none !important;',
      '  margin-right: 0.45em !important;',
      '  flex-shrink: 0 !important;',
      '}',
      '.cab-rb svg.cab-icon *,',
      '.cab-rb svg.cab-icon path,',
      '.cab-rb svg.cab-icon circle,',
      '.cab-rb svg.cab-icon rect,',
      '.cab-rb svg.cab-icon line,',
      '.cab-rb svg.cab-icon polyline,',
      '.cab-rb svg.cab-icon polygon {',
      '  fill: none !important;',
      '  stroke: currentColor !important;',
      '}',
      '.cab-rb .header-icon .cab-icon,',
      '.cab-rb .step-icon .cab-icon,',
      '.cab-rb .header-icon svg.cab-icon,',
      '.cab-rb .step-icon svg.cab-icon {',
      '  width: 1.6em !important;',
      '  height: 1.6em !important;',
      '  margin-right: 0 !important;',
      '}',
      '.cab-rb .tasting-title .cab-icon,',
      '.cab-rb .tasting-title svg.cab-icon,',
      '.cab-rb .cart-title .cab-icon,',
      '.cab-rb .cart-title svg.cab-icon {',
      '  width: 24px !important;',
      '  height: 24px !important;',
      '  vertical-align: -6px !important;',
      '}',

      '.cab-rb .match-details {',
      '  width: 100% !important;',
      '  box-sizing: border-box !important;',
      '  margin-top: 16px !important;',
      '  margin-bottom: 24px !important;',
      '}',

      '.cab-rb .beer-glass-img,',
      '.cab-rb .cab-water-glass,',
      '.cab-rb .cab-high-srm-glass {',
      '  width: 175% !important;',
      '  height: 175% !important;',
      '  object-fit: contain !important;',
      '  position: absolute !important;',
      '  top: 50% !important;',
      '  left: 50% !important;',
      '  transform: translate(-50%, -50%) !important;',
      '}',
      '.cab-rb .beer-glass {',
      '  position: relative !important;',
      '  overflow: hidden !important;',
      '}',

      '.cab-rb .sidebar {',
      '  max-height: 100% !important;',
      '  height: auto !important;',
      '  background: none !important;',
      '  background-color: transparent !important;',
      '}',

      '.cab-rb .cab-restore-banner {',
      '  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%) !important;',
      '  border: 1.5px solid #f59e0b !important;',
      '  border-radius: 14px !important;',
      '  padding: 28px 36px !important;',
      '  margin: 8px 0 36px 0 !important;',
      '  width: 100% !important;',
      '  display: flex !important;',
      '  align-items: center !important;',
      '  justify-content: space-between !important;',
      '  gap: 32px !important;',
      '  color: #78350f !important;',
      '  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.12) !important;',
      '  box-sizing: border-box !important;',
      '  font-family: inherit !important;',
      '}',
      '.cab-rb .cab-restore-banner-icon {',
      '  flex-shrink: 0 !important;',
      '  width: 44px !important;',
      '  height: 44px !important;',
      '  background: #f59e0b !important;',
      '  border-radius: 50% !important;',
      '  display: flex !important;',
      '  align-items: center !important;',
      '  justify-content: center !important;',
      '  font-size: 1.3rem !important;',
      '  padding: 0 !important;',
      '}',
      '.cab-rb .cab-restore-banner-icon svg,',
      '.cab-rb .cab-restore-banner-icon svg.cab-icon,',
      '.cab-rb .cab-restore-banner-icon i {',
      '  vertical-align: middle !important;',
      '  display: block !important;',
      '  margin: 0 !important;',
      '  width: 22px !important;',
      '  height: 22px !important;',
      '  stroke: #ffffff !important;',
      '}',
      '.cab-rb .cab-restore-banner-text {',
      '  flex: 1 !important;',
      '  line-height: 1.5 !important;',
      '  font-size: 0.95rem !important;',
      '}',
      '.cab-rb .cab-restore-banner-text strong {',
      '  display: block !important;',
      '  font-size: 1.05rem !important;',
      '  font-weight: 700 !important;',
      '  margin-bottom: 2px !important;',
      '  color: #78350f !important;',
      '}',
      '.cab-rb .cab-restore-banner-text span {',
      '  color: #92400e !important;',
      '  opacity: 0.85 !important;',
      '}',
      '.cab-rb .cab-restore-banner-actions {',
      '  display: flex !important;',
      '  gap: 14px !important;',
      '  align-items: center !important;',
      '  flex-shrink: 0 !important;',
      '}',
      '.cab-rb .cab-restore-banner-btn {',
      '  background: #ffffff !important;',
      '  border: 1.5px solid #f59e0b !important;',
      '  color: #92400e !important;',
      '  padding: 14px 28px !important;',
      '  border-radius: 8px !important;',
      '  font-size: 0.92rem !important;',
      '  font-weight: 600 !important;',
      '  cursor: pointer !important;',
      '  white-space: nowrap !important;',
      '  transition: all 0.2s ease !important;',
      '  font-family: inherit !important;',
      '  line-height: 1 !important;',
      '}',
      '.cab-rb .cab-restore-banner-btn:hover {',
      '  background: #f59e0b !important;',
      '  color: #ffffff !important;',
      '  transform: translateY(-1px) !important;',
      '  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.3) !important;',
      '}',
      '.cab-rb .cab-restore-banner-close {',
      '  background: transparent !important;',
      '  border: none !important;',
      '  color: #92400e !important;',
      '  cursor: pointer !important;',
      '  font-size: 1.6rem !important;',
      '  line-height: 1 !important;',
      '  padding: 6px 12px !important;',
      '  opacity: 0.5 !important;',
      '  transition: opacity 0.15s ease !important;',
      '  font-family: inherit !important;',
      '}',
      '.cab-rb .cab-restore-banner-close:hover { opacity: 1 !important; }',
      '@media (max-width: 640px) {',
      '  .cab-rb .cab-restore-banner {',
      '    flex-direction: column !important;',
      '    align-items: flex-start !important;',
      '    gap: 16px !important;',
      '    padding: 20px 22px !important;',
      '  }',
      '  .cab-rb .cab-restore-banner-actions { width: 100% !important; justify-content: flex-end !important; }',
      '}'
    ].join('\n');

    var style = document.createElement('style');
    style.id = 'cab-enhancements-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // ============================================================
  // 3. localStorage auto-save
  // ============================================================
  function storageAvailable() {
    try {
      var test = '__cab_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  function wrapRecalculate() {
    var original = window.recalculate;
    if (typeof original !== 'function') return;
    window.recalculate = function () {
      var srmBadge = document.querySelector('.cab-rb .srm-badge');
      if (srmBadge && srmBadge.dataset.cabWaterOverride === '1') {
        srmBadge.style.removeProperty('background-color');
        srmBadge.style.removeProperty('background');
        srmBadge.style.removeProperty('color');
        srmBadge.style.removeProperty('border');
        delete srmBadge.dataset.cabWaterOverride;
      }
      original.apply(this, arguments);
      updateFermentableInfoBoxes();
      updateBeerGlassForLowSRM();
      injectHopInfoBars();
      updateHopInfoBars();
      if (!isRestoring) scheduleSave();
    };
  }

  // ============================================================
  // SRM image override (water + high-SRM clamp)
  // ============================================================
  var SRM_IMAGE_BASE = window.SRM_IMAGE_BASE || 'https://craftabrew.com/cdn/shop/t/38/assets/';
  var SRM_0_URL = SRM_IMAGE_BASE + 'SRM-0.png';
  var MAX_SRM_IMAGE = 40;
  var EASTER_EGG_SRM = 100;

  function updateBeerGlassForLowSRM() {
    var srmEl = document.getElementById('srmValue');
    if (!srmEl) return;
    var srm = parseFloat(srmEl.textContent.trim());
    if (isNaN(srm)) srm = 0;

    var hasFermentables = false;
    document.querySelectorAll('#fermentablesBody tr').forEach(function (row) {
      var sel = row.querySelector('.ingredient-select');
      var amtInput = row.querySelector('.amount-input');
      if (sel && sel.value && amtInput && parseFloat(amtInput.value) > 0) {
        hasFermentables = true;
      }
    });

    var glassDiv = document.querySelector('.cab-rb .beer-glass');
    if (!glassDiv) return;

    var showWater = !hasFermentables || srm < 1;
    var showHighSrm = !showWater && srm > MAX_SRM_IMAGE;
    var showCore = !showWater && !showHighSrm;

    var waterImg = glassDiv.querySelector('img.cab-water-glass');
    var highSrmImg = glassDiv.querySelector('img.cab-high-srm-glass');

    if (showWater && !waterImg) {
      waterImg = document.createElement('img');
      waterImg.className = 'cab-water-glass';
      waterImg.src = SRM_0_URL;
      waterImg.alt = 'Water';
      glassDiv.appendChild(waterImg);
    }

    if (showHighSrm && !highSrmImg) {
      highSrmImg = document.createElement('img');
      highSrmImg.className = 'cab-high-srm-glass';
      highSrmImg.alt = 'Beer';
      glassDiv.appendChild(highSrmImg);
    }

    if (showHighSrm && highSrmImg) {
      var targetSrm = srm >= EASTER_EGG_SRM ? EASTER_EGG_SRM : MAX_SRM_IMAGE;
      var targetUrl = SRM_IMAGE_BASE + 'SRM-' + targetSrm + '.png';
      if (highSrmImg.src !== targetUrl) {
        highSrmImg.src = targetUrl;
      }
    }

    Array.prototype.slice.call(glassDiv.children).forEach(function (child) {
      if (child === waterImg) {
        child.style.display = showWater ? '' : 'none';
      } else if (child === highSrmImg) {
        child.style.display = showHighSrm ? '' : 'none';
      } else {
        child.style.display = showCore ? '' : 'none';
      }
    });

    if (showWater || showHighSrm) {
      glassDiv.style.setProperty('background', 'none', 'important');
      glassDiv.style.setProperty('background-color', 'transparent', 'important');
    } else {
      glassDiv.style.removeProperty('background');
      glassDiv.style.removeProperty('background-color');
    }

    function applySrmBadgeOverride() {
      var srmBadge = document.querySelector('.cab-rb .srm-badge');
      if (!srmBadge) return;
      if (showWater) {
        srmBadge.textContent = 'SRM: 0';
        srmBadge.style.setProperty('background-color', 'transparent', 'important');
        srmBadge.style.setProperty('background', 'transparent', 'important');
        srmBadge.style.setProperty('color', '#FFFFFF', 'important');
        srmBadge.style.setProperty('border', '1px solid rgba(255,255,255,0.5)', 'important');
        srmBadge.dataset.cabWaterOverride = '1';
      }
    }
    applySrmBadgeOverride();
    setTimeout(applySrmBadgeOverride, 0);
  }

  // ============================================================
  // Show PPG / °L / OG / PTS info for ALL fermentables
  // ============================================================
  function updateFermentableInfoBoxes() {
    if (!window.PRODUCTS || !window.PRODUCTS.fermentables) return;
    var batchSize = parseFloat(getVal('batchSize')) || 5;
    var method = getVal('brewMethod');
    var efficiency = parseFloat(getVal('efficiencySelect')) / 100 || 0.72;

    document.querySelectorAll('#fermentablesBody tr').forEach(function (row) {
      var sel = row.querySelector('.ingredient-select');
      var amtInput = row.querySelector('.amount-input');
      var unitSel = row.querySelector('.unit-select');
      var statsDiv = row.querySelector('.grain-stats');
      if (!sel || !sel.value || !statsDiv) return;

      var f = window.PRODUCTS.fermentables.find(function (x) { return x.id === sel.value; });
      if (!f) {
        statsDiv.style.display = 'none';
        return;
      }

      var rawAmt = parseFloat(amtInput ? amtInput.value : 0) || 0;
      var amt = unitSel && unitSel.value === 'oz' ? rawAmt / 16 : rawAmt;

      var gravityEff;
      if (method === 'extract') {
        if (f.type === 'dme' || f.type === 'sugar') gravityEff = 1;
        else if (f.cat === 'caramel') gravityEff = 0.3;
        else if (f.cat === 'roasted') gravityEff = 0.2;
        else if (f.cat === 'specialty') gravityEff = 0.15;
        else if (f.cat === 'base') gravityEff = 0.05;
        else if (f.cat === 'adjunct') gravityEff = 0.15;
        else gravityEff = 0.25;
      } else {
        if (f.type === 'dme' || f.type === 'sugar') gravityEff = 1;
        else gravityEff = efficiency;
      }

      var pts = f.ppg * amt * gravityEff;
      var ogContrib = 1 + pts / (batchSize * 1000);

      statsDiv.innerHTML =
        '<span><span class="stat-label">PPG:</span> ' + f.ppg + '</span>' +
        '<span><span class="stat-label">\u00B0L:</span> ' + f.lovibond + '</span>' +
        '<span><span class="stat-label">OG:</span> ' + ogContrib.toFixed(3) + '</span>' +
        '<span><span class="stat-label">PTS:</span> ' + Math.round(pts) + '</span>';
      statsDiv.style.display = 'block';
    });
  }

  // ============================================================
  // Add AA% to hop dropdown options
  // ============================================================
  function setupHopAADisplay() {
    var originalAddHop = window.addHop;
    if (typeof originalAddHop !== 'function') return;

    window.addHop = function () {
      originalAddHop.apply(this, arguments);
      updateLastHopRowOptions();
    };

    setTimeout(function () {
      document.querySelectorAll('#hopsBody tr').forEach(updateHopRowOptions);
    }, 100);
  }

  function updateLastHopRowOptions() {
    var rows = document.querySelectorAll('#hopsBody tr');
    if (!rows.length) return;
    updateHopRowOptions(rows[rows.length - 1]);
  }

  function updateHopRowOptions(row) {
    if (!row || !window.PRODUCTS || !window.PRODUCTS.hops) return;
    var sel = row.querySelector('.ingredient-select');
    if (!sel) return;
    Array.prototype.slice.call(sel.options).forEach(function (opt) {
      if (!opt.value) return;
      if (opt.textContent.indexOf('% AA') >= 0) return;
      var hop = window.PRODUCTS.hops.find(function (h) { return h.id === opt.value; });
      if (hop) {
        opt.textContent = hop.name + ' (' + hop.aa + '% AA)';
      }
    });
    var wrap = sel.closest('.search-select-wrap');
    if (wrap && wrap.refreshSearch) wrap.refreshSearch();
  }

  // ============================================================
  // Per-hop info bars: IBU contribution and Tinseth utilization
  // ============================================================
  function setupHopInfoBars() {
    var prevAddHop = window.addHop;
    if (typeof prevAddHop === 'function') {
      window.addHop = function () {
        prevAddHop.apply(this, arguments);
        setTimeout(function () {
          injectHopInfoBars();
          updateHopInfoBars();
        }, 0);
      };
    }
    setTimeout(function () {
      injectHopInfoBars();
      updateHopInfoBars();
    }, 200);
  }

  function injectHopInfoBars() {
    document.querySelectorAll('#hopsBody tr').forEach(function (row) {
      if (row.querySelector('.hop-stats')) return;
      var sel = row.querySelector('.ingredient-select');
      if (!sel) return;
      var container = sel.parentNode;
      if (!container) return;
      var statsDiv = document.createElement('div');
      statsDiv.className = 'grain-stats hop-stats';
      statsDiv.style.display = 'none';
      container.appendChild(statsDiv);
    });
  }

  function updateHopInfoBars() {
    var ogEl = document.getElementById('ogValue');
    var og = ogEl ? parseFloat(ogEl.textContent) : 1.050;
    if (isNaN(og) || og < 1.000) og = 1.050;
    var batchSize = parseFloat(getVal('batchSize')) || 5;
    var boilSize = parseFloat(getVal('boilSize')) || 6;
    var boilGravity = boilSize > 0 ? 1 + ((og - 1) * batchSize / boilSize) : og;

    document.querySelectorAll('#hopsBody tr').forEach(function (row) {
      var statsDiv = row.querySelector('.hop-stats');
      if (!statsDiv) return;
      var sel = row.querySelector('.ingredient-select');
      if (!sel || !sel.value) {
        statsDiv.style.display = 'none';
        return;
      }
      var inputs = row.querySelectorAll('.amount-input');
      var selects = row.querySelectorAll('select');
      if (inputs.length < 3) {
        statsDiv.style.display = 'none';
        return;
      }
      var amount = parseFloat(inputs[0].value) || 0;
      var aa = parseFloat(inputs[1].value) || 0;
      var time = parseFloat(inputs[2].value) || 0;
      var useId = (selects[1] ? selects[1].value : 'boil-60').toLowerCase();

      if (amount <= 0 || aa <= 0) {
        statsDiv.style.display = 'none';
        return;
      }

      var html;
      if (useId.indexOf('boil') === 0 && time > 0) {
        var bigness = 1.65 * Math.pow(0.000125, boilGravity - 1);
        var timeFactor = (1 - Math.exp(-0.04 * time)) / 4.15;
        var utilization = bigness * timeFactor;
        var ibu = (utilization * (aa / 100) * amount * 7490) / batchSize;
        html =
          '<span><span class="stat-label">IBU:</span> ' + ibu.toFixed(1) + '</span>' +
          '<span><span class="stat-label">UTIL:</span> ' + (utilization * 100).toFixed(1) + '%</span>';
      } else if (useId.indexOf('whirlpool') === 0) {
        html =
          '<span><span class="stat-label">IBU:</span> 0</span>' +
          '<span><span class="stat-label">USE:</span> WHIRLPOOL / AROMA</span>';
      } else if (useId.indexOf('dry') === 0) {
        html =
          '<span><span class="stat-label">IBU:</span> 0</span>' +
          '<span><span class="stat-label">USE:</span> DRY HOP / AROMA</span>';
      } else {
        html =
          '<span><span class="stat-label">IBU:</span> 0</span>' +
          '<span><span class="stat-label">USE:</span> AROMA</span>';
      }
      statsDiv.innerHTML = html;
      statsDiv.style.display = 'block';
    });
  }

  // ============================================================
  // Update the footnote in the Hops section
  // ============================================================
  function updateHopsNote() {
    var attempts = 0;
    function tryUpdate() {
      attempts++;
      var notes = document.querySelectorAll('.cab-rb *');
      var updated = false;
      notes.forEach(function (el) {
        if (el.children.length > 0) return;
        var txt = el.textContent || '';
        if (txt.indexOf('IBU calculated using Tinseth') >= 0 && !el.dataset.cabUpdatedNote) {
          el.innerHTML =
            '* AA% values are industry averages and vary by crop year and supplier.<br>' +
            '** IBU calculated using Tinseth formula for pellet hops.';
          el.dataset.cabUpdatedNote = '1';
          updated = true;
        }
      });
      if (!updated && attempts < 10) {
        setTimeout(tryUpdate, 150);
      }
    }
    tryUpdate();
  }

  // ============================================================
  // Yeast packet weight suffixes — v1.7.7 BASELINE, UNCHANGED
  // ============================================================
  function getYeastWeightSuffix(name) {
    if (!name) return '';
    var n = name.toLowerCase();

    if (n.indexOf('craft a brew') >= 0) return ' (2.5g \u2014 1 gal batch)';
    if (n.indexOf('kveik yeastery') >= 0) return ' (7g \u2014 5 gal)';
    if (n.indexOf('safale') >= 0 || n.indexOf('saflager') >= 0 || n.indexOf('safbrew') >= 0) return ' (11.5g \u2014 5 gal)';
    if (n.indexOf('wlp') >= 0 || n.indexOf('white labs') >= 0) return ' (11g \u2014 5 gal)';
    if (n.indexOf('lutra') >= 0 || n.indexOf('omega') >= 0) return ' (11g \u2014 5 gal)';
    if (n.indexOf('lalbrew') >= 0 || n.indexOf('lallemand') >= 0 || n.indexOf('danstar') >= 0) return ' (11g \u2014 5 gal)';
    if (n.indexOf('mangrove jack') >= 0) return ' (10g \u2014 5 gal)';

    var lalBrewStrains = ['nottingham', 'windsor', 'voss', 'verdant', 'abbaye', 'munich wheat', 'diamond lager', 'novalager', 'philly sour', 'new england', 'bry-97', 'koln'];
    for (var i = 0; i < lalBrewStrains.length; i++) {
      if (n.indexOf(lalBrewStrains[i]) >= 0) return ' (11g \u2014 5 gal)';
    }
    return '';
  }

  function setupYeastWeightDisplay() {
    var originalSetYeastType = window.setYeastType;
    if (typeof originalSetYeastType === 'function') {
      window.setYeastType = function () {
        originalSetYeastType.apply(this, arguments);
        setTimeout(updateYeastDropdownWeights, 50);
        setTimeout(updateYeastDropdownWeights, 300);
      };
    }
    // Initial-load retry: keep trying for up to 6 seconds in case the
    // yeast dropdown is built after our enhancements file initializes
    var attempts = 0;
    function tryUpdate() {
      attempts++;
      updateYeastDropdownWeights();
      if (attempts < 30) {
        setTimeout(tryUpdate, 200);
      }
    }
    tryUpdate();

    setTimeout(function attachObserver() {
      var sel = document.getElementById('yeastSelect');
      if (!sel) {
        setTimeout(attachObserver, 300);
        return;
      }
      try {
        var observer = new MutationObserver(function () {
          updateYeastDropdownWeights();
        });
        observer.observe(sel, { childList: true, subtree: true, characterData: true });
      } catch (e) {
        console.warn('[CAB] Yeast MutationObserver failed:', e);
      }
    }, 500);
  }

  function updateYeastDropdownWeights() {
    var sel = document.getElementById('yeastSelect');
    if (!sel || !sel.options || sel.options.length === 0) return false;

    if (window.currentYeastType && window.currentYeastType !== 'dry') {
      return true;
    }

    var didUpdate = false;
    Array.prototype.slice.call(sel.options).forEach(function (opt) {
      if (!opt.value) return;
      if (/\(\d+(\.\d+)?g/.test(opt.textContent)) return;
      var suffix = getYeastWeightSuffix(opt.textContent);
      if (suffix) {
        opt.textContent = opt.textContent + suffix;
        didUpdate = true;
      }
    });

    var wrap = sel.closest('.search-select-wrap');
    if (wrap) {
      if (typeof wrap.refreshSearch === 'function') {
        wrap.refreshSearch();
      }
      if (typeof wrap.rebuild === 'function') {
        wrap.rebuild();
      }
      try {
        var ev = new Event('change', { bubbles: true });
        sel.dispatchEvent(ev);
      } catch (e) {}
    }

    return didUpdate || sel.options.length > 1;
  }

  function scheduleSave() {
    if (!storageAvailable()) return;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(saveState, 500);
  }

  function gatherState() {
    var state = {
      version: 1,
      savedAt: Date.now(),
      batchSize: getVal('batchSize'),
      boilSize: getVal('boilSize'),
      brewMethod: getVal('brewMethod'),
      efficiency: getVal('efficiencySelect'),
      fermentables: [],
      hops: [],
      yeastType: window.currentYeastType || 'dry',
      yeastId: getVal('yeastSelect'),
      fermTemp: getVal('fermTemp'),
      packets: getVal('yeastPackets'),
      useStarter: !!(document.getElementById('useStarter') && document.getElementById('useStarter').checked),
      starterSize: getVal('starterSize'),
      starterCustom: getVal('starterCustom'),
      starterMethod: getVal('starterMethod'),
      styleId: getVal('styleSelect')
    };

    document.querySelectorAll('#fermentablesBody tr').forEach(function (row) {
      var sel = row.querySelector('.ingredient-select');
      if (!sel || !sel.value) return;
      var amtInput = row.querySelector('.amount-input');
      var unitSel = row.querySelector('.unit-select');
      var crushedInput = row.querySelector('.crushed-check input');
      state.fermentables.push({
        id: sel.value,
        amount: amtInput ? amtInput.value : '1',
        unit: unitSel ? unitSel.value : 'lb',
        crushed: crushedInput ? crushedInput.checked : true
      });
    });

    document.querySelectorAll('#hopsBody tr').forEach(function (row) {
      var sel = row.querySelector('.ingredient-select');
      if (!sel || !sel.value) return;
      var selects = row.querySelectorAll('select');
      var inputs = row.querySelectorAll('.amount-input');
      if (inputs.length < 3) return;
      state.hops.push({
        id: sel.value,
        amount: inputs[0].value,
        aa: inputs[1].value,
        useId: selects[1] ? selects[1].value : 'boil-60',
        time: inputs[2].value
      });
    });

    return state;
  }

  function saveState() {
    try {
      var state = gatherState();
      var hasData = state.fermentables.length || state.hops.length || state.yeastId;
      if (hasData) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      }
    } catch (e) {
      console.warn('[CAB] Save failed:', e);
    }
  }

  function loadState() {
    if (!storageAvailable()) return null;
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var state = JSON.parse(raw);
      var ageMs = Date.now() - (state.savedAt || 0);
      var maxAgeMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      if (ageMs > maxAgeMs) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return state;
    } catch (e) {
      console.warn('[CAB] Load failed:', e);
      return null;
    }
  }

  function clearState() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  // ============================================================
  // 4. Restore saved recipe + welcome banner
  // ============================================================
  function restoreRecipe() {
    var state = loadState();
    if (!state) return;

    isRestoring = true;

    try {
      setVal('batchSize', state.batchSize);
      setVal('boilSize', state.boilSize);
      setVal('brewMethod', state.brewMethod);
      if (typeof window.updateMethodDisplay === 'function') {
        window.updateMethodDisplay();
      }
      setVal('efficiencySelect', state.efficiency);

      (state.fermentables || []).forEach(function (f) {
        if (typeof window.addFermentable !== 'function') return;
        window.addFermentable();
        var rows = document.querySelectorAll('#fermentablesBody tr');
        var row = rows[rows.length - 1];
        if (!row) return;
        var sel = row.querySelector('.ingredient-select');
        if (sel) {
          sel.value = f.id;
          if (sel.parentNode && sel.parentNode.refreshSearch) {
            sel.parentNode.refreshSearch();
          }
          if (typeof window.onFermChange === 'function') window.onFermChange(sel);
        }
        var amtInput = row.querySelector('.amount-input');
        if (amtInput && f.amount != null) amtInput.value = f.amount;
        var unitSel = row.querySelector('.unit-select');
        if (unitSel && f.unit) unitSel.value = f.unit;
        var crushedInput = row.querySelector('.crushed-check input');
        if (crushedInput) crushedInput.checked = !!f.crushed;
      });

      (state.hops || []).forEach(function (h) {
        if (typeof window.addHop !== 'function') return;
        window.addHop();
        var rows = document.querySelectorAll('#hopsBody tr');
        var row = rows[rows.length - 1];
        if (!row) return;
        var sel = row.querySelector('.ingredient-select');
        if (sel) {
          sel.value = h.id;
          if (sel.parentNode && sel.parentNode.refreshSearch) {
            sel.parentNode.refreshSearch();
          }
          if (typeof window.onHopChange === 'function') window.onHopChange(sel);
        }
        var inputs = row.querySelectorAll('.amount-input');
        var selects = row.querySelectorAll('select');
        if (selects[1] && h.useId) selects[1].value = h.useId;
        if (inputs[0] && h.amount != null) inputs[0].value = h.amount;
        if (inputs[1] && h.aa != null) inputs[1].value = h.aa;
        if (inputs[2] && h.time != null) inputs[2].value = h.time;
      });

      // ----------------------------------------------------------
      // YEAST RESTORE: SKIPPED — v1.7.17
      // ----------------------------------------------------------
      // After eight rounds of attempts (v1.7.8-v1.7.16), yeast auto-
      // restore is not supported. The yeast IS still saved in
      // localStorage (gatherState captures it normally), and the
      // welcome-back banner below shows the last-used yeast name
      // so the user can re-pick with one click.

      setVal('fermTemp', state.fermTemp);
      setVal('yeastPackets', state.packets);

      if (state.useStarter) {
        var starterCheck = document.getElementById('useStarter');
        if (starterCheck) {
          starterCheck.checked = true;
          if (typeof window.toggleStarter === 'function') window.toggleStarter();
        }
        setVal('starterSize', state.starterSize);
        if (state.starterCustom) setVal('starterCustom', state.starterCustom);
        setVal('starterMethod', state.starterMethod);
        if (typeof window.onStarterSizeChange === 'function') {
          window.onStarterSizeChange();
        }
      }

      if (state.styleId) {
        setVal('styleSelect', state.styleId);
        if (typeof window.onStyleSelect === 'function') window.onStyleSelect();
      }

      if (typeof window.recalculate === 'function') window.recalculate();

      showRestoreBanner(state.savedAt, state);

    } catch (e) {
      console.error('[CAB] Restore failed:', e);
    } finally {
      isRestoring = false;
    }
  }

  function showRestoreBanner(savedAt, state) {
    var mainContent = document.querySelector('.cab-rb .main-content');
    if (!mainContent) return;

    var date = new Date(savedAt);
    var now = new Date();
    var diffDays = Math.floor((now - date) / (24 * 60 * 60 * 1000));
    var dateText;
    if (diffDays === 0) dateText = 'earlier today';
    else if (diffDays === 1) dateText = 'yesterday';
    else dateText = diffDays + ' days ago';

    // v1.7.17: show the last-used yeast name so the user knows
    // what to re-pick. Yeast auto-restore is disabled in this
    // version, so this gentle reminder gives them a one-click path.
    var yeastReminder = '';
    if (state && state.yeastId && window.PRODUCTS) {
      var yeastList = state.yeastType === 'liquid'
        ? window.PRODUCTS.yeastLiquid
        : window.PRODUCTS.yeastDry;
      if (yeastList) {
        var savedYeast = null;
        for (var i = 0; i < yeastList.length; i++) {
          if (yeastList[i].id === state.yeastId) {
            savedYeast = yeastList[i];
            break;
          }
        }
        if (savedYeast && savedYeast.name) {
          yeastReminder =
            ' <strong style="color:#78350f;">Last used yeast:</strong> ' +
            escapeHtml(savedYeast.name) + ' \u2014 please re-select below.';
        }
      }
    }

    var banner = document.createElement('div');
    banner.className = 'cab-restore-banner';
    banner.innerHTML =
      '<div class="cab-restore-banner-icon">\uD83D\uDCCB</div>' +
      '<div class="cab-restore-banner-text">' +
      '<strong>Welcome back!</strong>' +
      '<span>We restored your recipe from ' + dateText + '.' + yeastReminder + '</span>' +
      '</div>' +
      '<div class="cab-restore-banner-actions">' +
      '<button class="cab-restore-banner-btn" type="button">Clear Recipe</button>' +
      '<button class="cab-restore-banner-close" type="button" aria-label="Dismiss">\u00D7</button>' +
      '</div>';

    mainContent.insertBefore(banner, mainContent.firstChild);

    banner.querySelector('.cab-restore-banner-btn').addEventListener('click', function () {
      if (confirm('Clear your saved recipe and start fresh? This cannot be undone.')) {
        clearState();
        window.location.reload();
      }
    });
    banner.querySelector('.cab-restore-banner-close').addEventListener('click', function () {
      banner.remove();
    });
  }

  // Small utility: escape HTML entities in user-facing strings
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ============================================================
  // 6. Smart leave-page warning
  // ============================================================
  function setupLeavePageWarning() {
    if (storageAvailable()) return;

    window.addEventListener('beforeunload', function (e) {
      var hasFerm = document.querySelectorAll('#fermentablesBody tr').length > 0;
      var hasHops = document.querySelectorAll('#hopsBody tr').length > 0;
      var yeastSel = document.getElementById('yeastSelect');
      var hasYeast = yeastSel && yeastSel.value;
      if (hasFerm || hasHops || hasYeast) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    });
  }

  // ============================================================
  // Lucide icon replacement
  // ============================================================
  function replaceEmojisWithIcons() {
    if (window.lucide) {
      doIconReplacements();
      return;
    }
    var s = document.createElement('script');
    s.src = 'https://unpkg.com/lucide@latest/dist/umd/lucide.min.js';
    s.onload = function () { doIconReplacements(); };
    s.onerror = function () {
      console.warn('[CAB] Lucide icon library failed to load — keeping emojis.');
    };
    document.head.appendChild(s);
  }

  function doIconReplacements() {
    try {
      var headerIcon = document.querySelector('.cab-rb .header-icon');
      if (headerIcon) {
        headerIcon.innerHTML = '<i data-lucide="notebook-pen" class="cab-icon"></i>';
      }

      var stepIcons = document.querySelectorAll('.cab-rb .step-icon');
      var stepNames = ['clipboard-list', 'scale', 'shopping-cart'];
      stepIcons.forEach(function (el, i) {
        if (stepNames[i]) {
          el.innerHTML = '<i data-lucide="' + stepNames[i] + '" class="cab-icon"></i>';
        }
      });

      document.querySelectorAll('.cab-rb .card-title').forEach(function (el) {
        var text = el.textContent || '';
        if (text.indexOf('FERMENTABLES') >= 0) {
          el.innerHTML = '<i data-lucide="wheat" class="cab-icon"></i>FERMENTABLES';
        } else if (text.indexOf('HOPS') >= 0) {
          el.innerHTML = '<i data-lucide="hop" class="cab-icon"></i>HOPS';
        } else if (text.indexOf('YEAST') >= 0) {
          el.innerHTML = '<i data-lucide="flask-conical" class="cab-icon"></i>YEAST';
        }
      });

      document.querySelectorAll('.cab-rb .yeast-toggle-btn').forEach(function (btn) {
        var onclickAttr = btn.getAttribute('onclick') || '';
        if (onclickAttr.indexOf("'dry'") >= 0) {
          btn.innerHTML = '<i data-lucide="box" class="cab-icon"></i>Dry Yeast';
        } else if (onclickAttr.indexOf("'liquid'") >= 0) {
          btn.innerHTML = '<i data-lucide="droplet" class="cab-icon"></i>Liquid Yeast';
        }
      });

      document.querySelectorAll('.cab-rb .ferm-section-title').forEach(function (el) {
        if ((el.textContent || '').indexOf('FERMENTATION SETTINGS') >= 0) {
          el.innerHTML = '<i data-lucide="thermometer" class="cab-icon"></i>FERMENTATION SETTINGS';
        }
      });

      var timelineTitle = document.querySelector('.cab-rb .timeline-title');
      if (timelineTitle) {
        timelineTitle.innerHTML = '<i data-lucide="chart-line" class="cab-icon"></i>FERMENTATION TIMELINE';
      }

      var tastingTitle = document.querySelector('.cab-rb .tasting-title');
      if (tastingTitle) {
        tastingTitle.innerHTML = '<i data-lucide="beer" class="cab-icon"></i>TASTING NOTES PREVIEW';
      }

      var cartTitle = document.querySelector('.cab-rb .cart-title');
      if (cartTitle) {
        cartTitle.innerHTML = '<i data-lucide="shopping-cart" class="cab-icon"></i>SHOPPING LIST';
      }

      replaceTipBoxIcons();

      var autoOpt = document.querySelector('.cab-rb #styleSelect option[value="auto"]');
      if (autoOpt) {
        autoOpt.textContent = autoOpt.textContent.replace(/^\uD83C\uDFAF\s*/, '');
      }

      var bannerIcon = document.querySelector('.cab-restore-banner-icon');
      if (bannerIcon) {
        bannerIcon.innerHTML = '<i data-lucide="bookmark" class="cab-icon" style="width:1.4em;height:1.4em;margin:0;stroke:#ffffff;"></i>';
      }

      if (window.lucide && typeof window.lucide.createIcons === 'function') {
        window.lucide.createIcons();
      }

      if (!window._cabMethodDisplayWrapped && typeof window.updateMethodDisplay === 'function') {
        var originalMethodDisplay = window.updateMethodDisplay;
        window.updateMethodDisplay = function () {
          originalMethodDisplay.apply(this, arguments);
          setTimeout(function () {
            replaceTipBoxIcons();
            if (window.lucide && typeof window.lucide.createIcons === 'function') {
              window.lucide.createIcons();
            }
          }, 0);
        };
        window._cabMethodDisplayWrapped = true;
      }

    } catch (e) {
      console.error('[CAB] Icon replacement failed:', e);
    }
  }

  function replaceTipBoxIcons() {
    document.querySelectorAll('.cab-rb .tip-box strong').forEach(function (strong) {
      var text = strong.textContent || '';
      if (text.indexOf('Tip:') >= 0) {
        strong.innerHTML = '<i data-lucide="lightbulb" class="cab-icon"></i>Tip:';
      }
    });
  }

  // ============================================================
  // Utilities
  // ============================================================
  function getVal(id) {
    var el = document.getElementById(id);
    return el ? el.value : '';
  }
  function setVal(id, val) {
    var el = document.getElementById(id);
    if (el && val != null && val !== '') el.value = val;
  }
})();